import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

// Always mounted, visibility driven by `file` so open/close transitions animate.
// `file` is the `viewerFile` returned by useFileViewer.
export default function FileViewerDialog({ file, onClose, onDownload }) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const isSmallScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  // Keeps the last file rendered while the close transition plays
  const [displayedFile, setDisplayedFile] = React.useState(file);
  const fullScreen = isSmallScreen || isFullScreen;
  const isImage = displayedFile?.mimeType?.startsWith("image/");

  React.useEffect(() => {
    if (file) {
      setDisplayedFile(file);
    }
  }, [file]);

  function handleExited() {
    setIsFullScreen(false);
    setDisplayedFile(null);
  }

  return (
    <MUI.Dialog
      open={Boolean(file)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
      TransitionProps={{ onExited: handleExited }}
      data-testid="file-viewer.dialog"
    >
      <MUI.DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          py: 1,
        }}
      >
        <MUI.Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
          <MUI.Tooltip title={t("Download")}>
            <MUI.IconButton
              onClick={onDownload}
              size="small"
              data-testid="file-viewer.download-button"
            >
              <DownloadIcon fontSize="small" />
            </MUI.IconButton>
          </MUI.Tooltip>
          {!isSmallScreen && (
            <MUI.Tooltip title={t(isFullScreen ? "ExitFullScreen" : "FullScreen")}>
              <MUI.IconButton
                onClick={() => setIsFullScreen((prev) => !prev)}
                size="small"
                data-testid="file-viewer.fullscreen-button"
              >
                {isFullScreen ? (
                  <FullscreenExitIcon fontSize="small" />
                ) : (
                  <FullscreenIcon fontSize="small" />
                )}
              </MUI.IconButton>
            </MUI.Tooltip>
          )}
          <MUI.Typography variant="subtitle1" fontWeight={600} noWrap title={displayedFile?.name}>
            {displayedFile?.name}
          </MUI.Typography>
        </MUI.Stack>
        <MUI.IconButton
          onClick={onClose}
          size="small"
          title={t("Close")}
          data-testid="file-viewer.close-button"
        >
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.DialogTitle>
      <MUI.DialogContent
        dividers
        sx={{
          height: fullScreen ? "100%" : "80vh",
          p: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {displayedFile ? (
          isImage ? (
            <img
              src={displayedFile.url}
              alt={displayedFile.name}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <iframe
              src={displayedFile.url}
              title={displayedFile.name}
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          )
        ) : null}
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}
